import type { OneBotIngressHealth, OneBotIngressVerdict, OneBotRuntimePayload } from "../api/types";

export type OneBotLevelClass = "ok" | "warning" | "error";

export function onebotStateLabel(value: unknown) {
  const labels: Record<string, string> = {
    connected: "已连接",
    connecting: "连接中",
    disconnected: "未连接",
    idle: "未启动",
    missing: "未初始化",
    stopping: "停止中",
    unknown: "未知"
  };
  return labels[String(value || "")] || String(value || "未连接");
}

export function onebotDiagnosisLabel(value: unknown) {
  const labels: Record<string, string> = {
    connected: "连接正常",
    connected_waiting_for_messages: "等待首条消息",
    waiting_for_first_message_event: "等待首条消息",
    connected_no_message_events_after_grace: "疑似推送失效",
    onebot_push_stale: "疑似推送失效",
    connected_but_no_recent_events: "近期无事件",
    no_active_websocket: "WebSocket 未连接",
    onebot_disconnected: "OneBot 断开",
    onebot_client_missing: "OneBot 客户端未初始化",
    status_unavailable: "状态不可读",
    refused: "连接被拒绝",
    error: "连接异常",
    closed: "连接关闭",
    manual_admin_reconnect: "手动重连中"
  };
  return labels[String(value || "")] || String(value || "-");
}

export function onebotIngressLabel(value: unknown) {
  const labels: Record<string, string> = {
    reply_ready: "入口正常",
    qq_not_logged_in: "QQ 未登录",
    qq_offline: "QQ 离线",
    onebot_refused: "连接被拒绝",
    onebot_disconnected: "OneBot 断开",
    onebot_push_stale: "推送疑似失效",
    connected_no_message_events_after_grace: "推送疑似失效",
    waiting_for_first_message_event: "等待首条消息",
    onebot_not_reply_ready: "未证明可回复"
  };
  return labels[String(value || "")] || String(value || "-");
}

export function onebotRecoveryActionLabel(value: unknown) {
  const labels: Record<string, string> = {
    login_qq: "登录 QQ",
    login_qq_or_check_onebot: "登录 QQ / 检查 OneBot",
    send_test_message: "发送测试消息",
    reconnect_onebot: "重连 OneBot",
    reconnect_onebot_or_restart_napcat: "重连 OneBot / 重启 NapCat",
    restart_bot_or_onebot: "重启 bot 或 OneBot",
    inspect_onebot_status: "检查 OneBot 状态",
    inspect_bot_logs: "检查 bot 日志",
    inspect_delivery: "检查发送链路",
    inspect_trace: "检查 Trace",
    adjust_window: "调整诊断窗口"
  };
  return labels[String(value || "")] || String(value || "-");
}

export function onebotIngressLevelLabel(value: unknown, reason?: unknown) {
  const normalizedReason = String(reason || "");
  if (normalizedReason === "waiting_for_first_message_event") return "等待首条消息";
  if (normalizedReason === "connected_no_message_events_after_grace" || normalizedReason === "onebot_push_stale") {
    return "推送疑似失效";
  }
  const labels: Record<string, string> = {
    ok: "正常",
    waiting: "等待消息",
    warning: "入站静默",
    error: "入口异常"
  };
  return labels[String(value || "")] || String(value || "-");
}

export function onebotIngressLevelClass(value: unknown): OneBotLevelClass {
  const level = String(value || "");
  if (level === "ok") return "ok";
  if (level === "warning" || level === "waiting") return "warning";
  return "error";
}

export function onebotReplyReadyLabel(runtime?: OneBotRuntimePayload) {
  return runtime?.reply_ready ? "可回复" : "未证明";
}

export function onebotIngressReason(runtime?: OneBotRuntimePayload, ingress?: OneBotIngressHealth) {
  return String(ingress?.reason || runtime?.reply_blocker || runtime?.diagnosis || "").trim();
}

export function onebotIngressSummary(
  runtime: OneBotRuntimePayload | undefined,
  connected: boolean,
  ingress?: OneBotIngressHealth,
  verdict?: OneBotIngressVerdict
) {
  if (verdict?.detail) return String(verdict.detail);
  if (ingress?.summary) return String(ingress.summary);
  if (!connected) return "OneBot WebSocket 未连接，QQ 事件不会进入 bot。";
  const reason = onebotIngressReason(runtime, ingress);
  if (runtime?.message_event_count) return "OneBot message event 已进入 bot。";
  if (reason === "connected_no_message_events_after_grace" || reason === "onebot_push_stale") {
    return "QQ 在线且 WebSocket 已连接，但 NapCat 收到的 QQ 消息疑似没有推给 bot。";
  }
  if (reason === "waiting_for_first_message_event") {
    return "WebSocket 已连接，正在等待第一条 QQ 消息进入 bot。";
  }
  return "WebSocket 已连接，正在等待 QQ message event。";
}

export function onebotRecoveryHint(runtime?: OneBotRuntimePayload, ingress?: OneBotIngressHealth) {
  const reason = onebotIngressReason(runtime, ingress);
  const nextIn = formatSeconds(runtime?.next_reconnect_in_seconds ?? ingress?.next_reconnect_in_seconds);
  const autoReconnect = Boolean(runtime?.auto_reconnect_enabled || ingress?.auto_reconnect_enabled);
  if (reason === "connected_no_message_events_after_grace" || reason === "onebot_push_stale") {
    return nextIn !== "-"
      ? `NapCat 可能收到了 QQ 消息但没有推给 bot；系统会在约 ${nextIn} 后自动重连，也可以现在手动重连 OneBot。`
      : "NapCat 可能收到了 QQ 消息但没有推给 bot；请手动重连 OneBot，必要时重启 NapCat。";
  }
  if (reason === "waiting_for_first_message_event") {
    if (nextIn !== "-") return `如果 NapCat 已看到新消息但这里仍等待首条消息，系统会在约 ${nextIn} 后自动重连，也可以现在手动重连 OneBot。`;
    if (autoReconnect) return "如果 NapCat 已看到新消息但这里仍等待首条消息，系统已启用自动重连，也可以现在手动重连 OneBot。";
    return "如果 NapCat 已看到新消息但这里仍等待首条消息，请手动重连 OneBot。";
  }
  return String(ingress?.suggestion || "");
}

function formatSeconds(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  if (n > 86400) return ">1d";
  if (n > 3600) return `${Math.floor(n / 3600)}h`;
  if (n > 60) return `${Math.floor(n / 60)}m`;
  return `${Math.floor(n)}s`;
}
